import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  AlertTriangle, Printer, FileText, Loader2, Plus, Trash2,
  Armchair, DoorOpen, IdCard, Bookmark, Ban, X, Sparkles, Tag,
} from "lucide-react";

const CATEGORIES = [
  { id: "seat", icon: Armchair, tone: "#D4AF37",
    label: "Sitzschilder", sub: "24 Etiketten pro A4 · zum Aufkleben",
    desc: "Nummerierte Reihe/Platz-Aufkleber für einen ganzen Saal — nachdem der Sitzplan-Editor Sitze anlegt oder wenn Nummern abgehen." },
  { id: "door", icon: DoorOpen, tone: "#EEDC82",
    label: "Türschilder", sub: "A4 quer · pro Vorstellung",
    desc: "Großes Poster für die Saaltür: Saal · Filmtitel · Uhrzeit · FSK. Aus einer Vorstellung heraus generierbar." },
  { id: "badge", icon: IdCard, tone: "#8FBC8F",
    label: "Personalnamen-Schilder", sub: "10 Badges pro A4 · Kreditkarten-Größe",
    desc: "Anstecknamen für Kinomitarbeiter — direkt aus dem Team oder frei eingegeben." },
  { id: "reserved", icon: Bookmark, tone: "#EE9CA1",
    label: "Reserviert-Schilder", sub: "6 pro A4 · für Sitze",
    desc: '„RESERVIERT · Familie Müller"-Aushänge für Stammgäste — auch en bloc pro Vorstellung.' },
  { id: "price", icon: Tag, tone: "#EEDC82",
    label: "Preisschilder Snackbar", sub: "Menü-Wandkarte oder Einzelkarten",
    desc: "Snackbar-Preisliste als große Wandkarte (Popcorn 5€ · Cola 3€ …) oder als 4 Einzelkarten pro A4 — direkt aus Produkten der Datenbank." },
  { id: "status", icon: Ban, tone: "#7A1921",
    label: "Status-Aushänge", sub: "Ausverkauft · Geschlossen · Pause",
    desc: "A4-quer Schilder für die Kassentür, Foyer oder Saaleingang bei außergewöhnlichen Situationen." },
];

export default function PrintShop() {
  const [openCat, setOpenCat] = useState(null);
  return (
    <div className="space-y-6" data-testid="printshop-page">
      <div className="flex items-center gap-3">
        <Sparkles size={22} className="text-[#D4AF37]" />
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl">Druck-Werkstatt</h1>
          <div className="text-[10px] uppercase tracking-[0.32em] text-[#A8A398] mt-1">
            Notfall-Schilder · Aushänge · alles A4 · direkt an den Bürodrucker
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {CATEGORIES.map((cat) => {
          const Icon = cat.icon;
          return (
            <button key={cat.id} onClick={() => setOpenCat(cat.id)}
              data-testid={`ps-cat-${cat.id}`}
              className="card-lux p-5 text-left hover:border-[#D4AF37]/60 transitions-safe">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-full flex items-center justify-center shrink-0"
                  style={{ background: `${cat.tone}18`, color: cat.tone }}>
                  <Icon size={20} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-widest" style={{ color: cat.tone }}>{cat.sub}</div>
                  <div className="font-heading text-2xl">{cat.label}</div>
                  <div className="text-xs text-[#A8A398] mt-2 leading-relaxed">{cat.desc}</div>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {openCat === "seat" && <SeatLabelsDialog onClose={() => setOpenCat(null)} />}
      {openCat === "door" && <DoorSignDialog onClose={() => setOpenCat(null)} />}
      {openCat === "badge" && <NameBadgesDialog onClose={() => setOpenCat(null)} />}
      {openCat === "reserved" && <ReservedSignsDialog onClose={() => setOpenCat(null)} />}
      {openCat === "price" && <PriceSignsDialog onClose={() => setOpenCat(null)} />}
      {openCat === "status" && <StatusSignDialog onClose={() => setOpenCat(null)} />}
    </div>
  );
}

// ---------- gemeinsame UI-Bausteine ----------
function DialogShell({ title, subtitle, tone = "#D4AF37", icon: Icon, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-2 sm:p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-[#0A0A0A] border border-[#2A2A2A] rounded-xl p-6 w-full max-w-2xl my-8" onClick={(e) => e.stopPropagation()} data-testid={testid}>
        <div className="flex items-center gap-3 mb-5">
          <Icon size={20} style={{ color: tone }} />
          <div>
            <div className="font-heading text-2xl" style={{ color: tone }}>{title}</div>
            <div className="text-[10px] uppercase tracking-[0.32em] text-[#A8A398] mt-0.5">{subtitle}</div>
          </div>
          <button onClick={onClose} className="ml-auto text-[#575148] hover:text-[#F5F0E6]"><X size={20} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ActionButtons({ tone = "#D4AF37", onPreview, onPrint, busy, previewLabel = "PDF-Vorschau", printLabel = "Jetzt drucken" }) {
  return (
    <div className="flex gap-2 mt-5">
      <button onClick={onPreview} disabled={busy} data-testid="ps-preview"
        className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 border border-[#2A2A2A] rounded text-[10px] uppercase tracking-widest text-[#A8A398] hover:border-[#D4AF37] hover:text-[#D4AF37] disabled:opacity-40">
        <FileText size={12} /> {previewLabel}
      </button>
      <button onClick={onPrint} disabled={busy} data-testid="ps-print"
        className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded text-[10px] uppercase tracking-widest text-black disabled:opacity-40"
        style={{ background: tone }}>
        {busy ? <><Loader2 size={12} className="animate-spin" /> Sende …</> : <><Printer size={12} /> {printLabel}</>}
      </button>
    </div>
  );
}

async function postPdfPreview(endpoint, body) {
  const { data } = await api.post(endpoint, body, { responseType: "blob" });
  const url = URL.createObjectURL(data);
  window.open(url, "_blank");
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

async function postPrint(endpoint, body) {
  await api.post(`${endpoint}?print_office=true`, body);
}

// ---------- 1) Sitzschilder ----------
function SeatLabelsDialog({ onClose }) {
  const [hall, setHall] = useState("Saal 1");
  const [rows, setRows] = useState("A,B,C,D");
  const [perRow, setPerRow] = useState(10);
  const [busy, setBusy] = useState(false);
  const build = () => ({
    hall, rows: rows.split(",").map((r) => r.trim()).filter(Boolean),
    seats_per_row: Number(perRow) || 10,
  });
  const total = build().rows.length * (Number(perRow) || 10);
  const preview = async () => { setBusy(true); try { await postPdfPreview("/admin/signage/seat-labels", build()); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); } };
  const print = async () => { setBusy(true); try { await postPrint("/admin/signage/seat-labels", build()); toast.success(`${total} Sitzschilder an Drucker gesendet`); onClose(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Fehler"); } finally { setBusy(false); } };
  return (
    <DialogShell title="Sitzschilder" subtitle="Nummerierte Aufkleber · 24 pro A4" tone="#D4AF37" icon={Armchair} onClose={onClose} testid="ps-dialog-seat">
      <div className="space-y-4">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Saal-Name (auf Etiketten)</label>
          <input value={hall} onChange={(e) => setHall(e.target.value)}
            data-testid="ps-seat-hall"
            className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#D4AF37] outline-none" />
        </div>
        <div className="grid grid-cols-[1fr_120px] gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Reihen (Komma-getrennt)</label>
            <input value={rows} onChange={(e) => setRows(e.target.value)}
              data-testid="ps-seat-rows"
              placeholder="A,B,C,D,E"
              className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 font-mono focus:border-[#D4AF37] outline-none" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Sitze/Reihe</label>
            <input type="number" min={1} max={40} value={perRow}
              onChange={(e) => setPerRow(Math.max(1, Math.min(40, Number(e.target.value) || 1)))}
              data-testid="ps-seat-per-row"
              className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 font-mono focus:border-[#D4AF37] outline-none" />
          </div>
        </div>
        <div className="text-[10px] text-[#575148]">Ergibt {total} Etiketten · {Math.ceil(total / 24)} A4-Blatt · Grid 4×6 pro Blatt</div>
        <ActionButtons tone="#D4AF37" onPreview={preview} onPrint={print} busy={busy} />
      </div>
    </DialogShell>
  );
}

// ---------- 2) Türschilder ----------
function DoorSignDialog({ onClose }) {
  const [mode, setMode] = useState("manual");  // manual | showtime
  const [hall, setHall] = useState("Saal 1");
  const [movie, setMovie] = useState("");
  const [startTime, setStartTime] = useState("20:00");
  const [entryTime, setEntryTime] = useState("");
  const [date, setDate] = useState("");
  const [fsk, setFsk] = useState("");
  const [showtimes, setShowtimes] = useState([]);
  const [showtimeId, setShowtimeId] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/showtimes").then((r) => setShowtimes((r.data || []).slice(0, 40))).catch(() => {});
  }, []);

  const build = () => (mode === "showtime" ? { showtime_id: showtimeId, hall }
    : { hall, movie, start_time: startTime, entry_time: entryTime, date_str: date, fsk });
  const preview = async () => { setBusy(true); try { await postPdfPreview("/admin/signage/door-sign", build()); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); } };
  const print = async () => { setBusy(true); try { await postPrint("/admin/signage/door-sign", build()); toast.success("Türschild an Drucker gesendet"); onClose(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Fehler"); } finally { setBusy(false); } };

  return (
    <DialogShell title="Türschild" subtitle="A4 quer · für Saaltür" tone="#EEDC82" icon={DoorOpen} onClose={onClose} testid="ps-dialog-door">
      <div className="grid grid-cols-2 gap-1 mb-4 p-1 bg-[#050505] border border-[#1A1A1A] rounded">
        {[{ v: "manual", l: "Freie Eingabe" }, { v: "showtime", l: "Aus Vorstellung" }].map((m) => (
          <button key={m.v} onClick={() => setMode(m.v)} data-testid={`ps-door-mode-${m.v}`}
            className={`px-3 py-2 rounded text-[10px] uppercase tracking-widest ${mode === m.v ? "bg-[#D4AF37] text-black" : "text-[#A8A398]"}`}>{m.l}</button>
        ))}
      </div>
      <div className="space-y-3">
        {mode === "showtime" ? (
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Vorstellung wählen</label>
            <select value={showtimeId} onChange={(e) => setShowtimeId(e.target.value)}
              data-testid="ps-door-showtime"
              className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2">
              <option value="">— bitte wählen —</option>
              {showtimes.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.date} · {s.start_time} · {s.hall} · {s.movie_title || s.movie_id?.slice(0, 8)}
                </option>
              ))}
            </select>
            <label className="text-[10px] uppercase tracking-widest text-[#A8A398] mt-3 block">Saal-Bezeichnung (überschreiben)</label>
            <input value={hall} onChange={(e) => setHall(e.target.value)}
              className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2" />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-[1fr_100px] gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Saal</label>
                <input value={hall} onChange={(e) => setHall(e.target.value)} data-testid="ps-door-hall"
                  className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">FSK</label>
                <input value={fsk} onChange={(e) => setFsk(e.target.value)} placeholder="12"
                  className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 font-mono" />
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Filmtitel</label>
              <input value={movie} onChange={(e) => setMovie(e.target.value)} data-testid="ps-door-movie"
                className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2" />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Datum</label>
                <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                  className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 font-mono text-sm" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Beginn</label>
                <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)}
                  className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 font-mono text-sm" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Einlass</label>
                <input type="time" value={entryTime} onChange={(e) => setEntryTime(e.target.value)}
                  className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 font-mono text-sm" />
              </div>
            </div>
          </>
        )}
        <ActionButtons tone="#EEDC82" onPreview={preview} onPrint={print} busy={busy} />
      </div>
    </DialogShell>
  );
}

// ---------- 3) Personalnamen-Schilder ----------
function NameBadgesDialog({ onClose }) {
  const [items, setItems] = useState([{ name: "", role: "Kinomitarbeiter" }]);
  const [includeStaff, setIncludeStaff] = useState(false);
  const [busy, setBusy] = useState(false);
  const upd = (i, k, v) => setItems((arr) => arr.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const add = () => setItems((arr) => [...arr, { name: "", role: "Kinomitarbeiter" }]);
  const rm = (i) => setItems((arr) => arr.filter((_, idx) => idx !== i));
  const build = () => ({
    badges: items.filter((x) => x.name.trim()).map((x) => ({ name: x.name.trim(), role: x.role })),
    include_all_staff: includeStaff,
  });
  const preview = async () => { setBusy(true); try { await postPdfPreview("/admin/signage/name-badges", build()); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); } };
  const print = async () => { setBusy(true); try { await postPrint("/admin/signage/name-badges", build()); toast.success("Namensschilder an Drucker gesendet"); onClose(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Fehler"); } finally { setBusy(false); } };
  return (
    <DialogShell title="Personalnamen-Schilder" subtitle="10 Anstecknamen pro A4-Blatt" tone="#8FBC8F" icon={IdCard} onClose={onClose} testid="ps-dialog-badge">
      <div className="space-y-4">
        <label className="flex items-center gap-2 cursor-pointer text-sm">
          <input type="checkbox" checked={includeStaff} onChange={(e) => setIncludeStaff(e.target.checked)}
            data-testid="ps-badge-all-staff" className="accent-[#8FBC8F]" />
          Alle aktiven Team-Mitglieder aus der Datenbank übernehmen (Rolle wird automatisch beschriftet)
        </label>
        <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
          {items.map((it, i) => (
            <div key={i} className="grid grid-cols-[1fr_140px_28px] gap-2">
              <input value={it.name} onChange={(e) => upd(i, "name", e.target.value)}
                placeholder="Name" data-testid={`ps-badge-name-${i}`}
                className="bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2" />
              <select value={it.role} onChange={(e) => upd(i, "role", e.target.value)}
                data-testid={`ps-badge-role-${i}`}
                className="bg-[#050505] border border-[#2A2A2A] rounded px-2 py-2 text-sm">
                {["Kinomitarbeiter", "Kasse", "Snackbar", "Einlass", "Leitung", "Technik", "Praktikum", "Team"].map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
              <button onClick={() => rm(i)} className="border border-[#7A1921]/40 text-[#EE9CA1] rounded p-2 hover:bg-[#7A1921]/20"><Trash2 size={12} /></button>
            </div>
          ))}
        </div>
        <button onClick={add} data-testid="ps-badge-add"
          className="inline-flex items-center gap-1 px-3 py-2 text-[10px] uppercase tracking-widest border border-[#8FBC8F]/40 text-[#8FBC8F] rounded hover:bg-[#8FBC8F]/10">
          <Plus size={12} /> Name hinzufügen
        </button>
        <ActionButtons tone="#8FBC8F" onPreview={preview} onPrint={print} busy={busy} />
      </div>
    </DialogShell>
  );
}

// ---------- 4) Reserviert-Schilder ----------
function ReservedSignsDialog({ onClose }) {
  const [mode, setMode] = useState("manual");
  const [items, setItems] = useState([{ seat: "", name: "", note: "" }]);
  const [showtimes, setShowtimes] = useState([]);
  const [showtimeId, setShowtimeId] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/showtimes").then((r) => setShowtimes((r.data || []).slice(0, 40))).catch(() => {}); }, []);
  const upd = (i, k, v) => setItems((arr) => arr.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const add = () => setItems((arr) => [...arr, { seat: "", name: "", note: "" }]);
  const rm = (i) => setItems((arr) => arr.filter((_, idx) => idx !== i));
  const build = () => (mode === "showtime"
    ? { showtime_id: showtimeId }
    : { entries: items.filter((x) => (x.name.trim() || x.seat.trim())) });
  const preview = async () => { setBusy(true); try { await postPdfPreview("/admin/signage/reserved-signs", build()); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); } };
  const print = async () => { setBusy(true); try { await postPrint("/admin/signage/reserved-signs", build()); toast.success("Reserviert-Schilder an Drucker gesendet"); onClose(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Fehler"); } finally { setBusy(false); } };
  return (
    <DialogShell title="Reserviert-Schilder" subtitle="6 pro A4-Blatt · für Sitze" tone="#EE9CA1" icon={Bookmark} onClose={onClose} testid="ps-dialog-reserved">
      <div className="grid grid-cols-2 gap-1 mb-4 p-1 bg-[#050505] border border-[#1A1A1A] rounded">
        {[{ v: "manual", l: "Manuell" }, { v: "showtime", l: "Aus Vorstellung" }].map((m) => (
          <button key={m.v} onClick={() => setMode(m.v)}
            className={`px-3 py-2 rounded text-[10px] uppercase tracking-widest ${mode === m.v ? "bg-[#EE9CA1] text-black" : "text-[#A8A398]"}`}>{m.l}</button>
        ))}
      </div>
      {mode === "showtime" ? (
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Vorstellung</label>
          <select value={showtimeId} onChange={(e) => setShowtimeId(e.target.value)}
            data-testid="ps-reserved-showtime"
            className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2">
            <option value="">— bitte wählen —</option>
            {showtimes.map((s) => (
              <option key={s.id} value={s.id}>{s.date} · {s.start_time} · {s.hall} · {s.movie_title || s.movie_id?.slice(0, 8)}</option>
            ))}
          </select>
          <div className="mt-2 text-[10px] text-[#575148]">Zieht automatisch alle reservierten und bezahlten Tickets dieser Vorstellung.</div>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((it, i) => (
            <div key={i} className="grid grid-cols-[80px_1fr_1fr_28px] gap-2">
              <input value={it.seat} onChange={(e) => upd(i, "seat", e.target.value)}
                placeholder="C·07" className="bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 font-mono" />
              <input value={it.name} onChange={(e) => upd(i, "name", e.target.value)}
                placeholder="Familie Müller" className="bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2" />
              <input value={it.note} onChange={(e) => upd(i, "note", e.target.value)}
                placeholder="20:00 Interstellar" className="bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 text-xs" />
              <button onClick={() => rm(i)} className="border border-[#7A1921]/40 text-[#EE9CA1] rounded p-2 hover:bg-[#7A1921]/20"><Trash2 size={12} /></button>
            </div>
          ))}
          <button onClick={add}
            className="inline-flex items-center gap-1 px-3 py-2 text-[10px] uppercase tracking-widest border border-[#EE9CA1]/40 text-[#EE9CA1] rounded hover:bg-[#EE9CA1]/10">
            <Plus size={12} /> Reservierung hinzufügen
          </button>
        </div>
      )}
      <ActionButtons tone="#EE9CA1" onPreview={preview} onPrint={print} busy={busy} />
    </DialogShell>
  );
}

// ---------- 5) Status-Aushänge ----------
function PriceSignsDialog({ onClose }) {
  const [mode, setMode] = useState("db");    // db | custom
  const [layout, setLayout] = useState("menu");
  const [title, setTitle] = useState("SNACKBAR");
  const [subtitle, setSubtitle] = useState("Alle Preise inkl. MwSt.");
  const [category, setCategory] = useState("");
  const [items, setItems] = useState([{ name: "Popcorn Groß", price: 5, sub: "Süß · 100g" }]);
  const [categories, setCategories] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/products").then((r) => {
      const cats = [...new Set((r.data || []).map((p) => p.category).filter(Boolean))].sort();
      setCategories(cats);
    }).catch(() => {});
  }, []);
  const upd = (i, k, v) => setItems((arr) => arr.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const add = () => setItems((arr) => [...arr, { name: "", price: 0, sub: "" }]);
  const rm = (i) => setItems((arr) => arr.filter((_, idx) => idx !== i));
  const build = () => ({
    layout, title, subtitle,
    ...(mode === "db"
      ? { include_all_products: true, category: category || null }
      : { items: items.filter((x) => (x.name || "").trim()).map((x) => ({ ...x, price: Number(x.price) || 0 })) }),
  });
  const preview = async () => { setBusy(true); try { await postPdfPreview("/admin/signage/price-signs", build()); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); } };
  const print = async () => { setBusy(true); try { await postPrint("/admin/signage/price-signs", build()); toast.success("Preisschilder an Drucker gesendet"); onClose(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Fehler"); } finally { setBusy(false); } };
  return (
    <DialogShell title="Preisschilder Snackbar" subtitle="Menü-Wandkarte oder Einzelkarten" tone="#EEDC82" icon={Tag} onClose={onClose} testid="ps-dialog-price">
      <div className="grid grid-cols-2 gap-1 mb-3 p-1 bg-[#050505] border border-[#1A1A1A] rounded">
        {[{ v: "menu", l: "Menü-Wandkarte (1 Seite)" }, { v: "individual", l: "Einzelkarten (4 pro A4)" }].map((m) => (
          <button key={m.v} onClick={() => setLayout(m.v)}
            data-testid={`ps-price-layout-${m.v}`}
            className={`px-3 py-2 rounded text-[10px] uppercase tracking-widest ${layout === m.v ? "bg-[#EEDC82] text-black" : "text-[#A8A398]"}`}>{m.l}</button>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-1 mb-4 p-1 bg-[#050505] border border-[#1A1A1A] rounded">
        {[{ v: "db", l: "Aus Produkt-DB" }, { v: "custom", l: "Manuell eingeben" }].map((m) => (
          <button key={m.v} onClick={() => setMode(m.v)}
            data-testid={`ps-price-mode-${m.v}`}
            className={`px-3 py-2 rounded text-[10px] uppercase tracking-widest ${mode === m.v ? "bg-[#D4AF37] text-black" : "text-[#A8A398]"}`}>{m.l}</button>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-2 mb-4">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Titel</label>
          <input value={title} onChange={(e) => setTitle(e.target.value)}
            data-testid="ps-price-title"
            className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Untertitel</label>
          <input value={subtitle} onChange={(e) => setSubtitle(e.target.value)}
            className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 text-sm" />
        </div>
      </div>
      {mode === "db" ? (
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Kategorie-Filter (optional)</label>
          <select value={category} onChange={(e) => setCategory(e.target.value)}
            data-testid="ps-price-category"
            className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2">
            <option value="">— alle Kategorien —</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <div className="mt-2 text-[10px] text-[#575148]">Zieht alle verkäuflichen Produkte aus der DB automatisch mit aktuellen Preisen.</div>
        </div>
      ) : (
        <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
          {items.map((it, i) => (
            <div key={i} className="grid grid-cols-[1fr_90px_1fr_28px] gap-2">
              <input value={it.name} onChange={(e) => upd(i, "name", e.target.value)}
                placeholder="Produkt" className="bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2" />
              <input type="number" step="0.10" value={it.price} onChange={(e) => upd(i, "price", e.target.value)}
                placeholder="0,00" className="bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 font-mono text-right" />
              <input value={it.sub} onChange={(e) => upd(i, "sub", e.target.value)}
                placeholder="Süß · Groß" className="bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 text-xs" />
              <button onClick={() => rm(i)} className="border border-[#7A1921]/40 text-[#EE9CA1] rounded p-2 hover:bg-[#7A1921]/20"><Trash2 size={12} /></button>
            </div>
          ))}
          <button onClick={add}
            className="inline-flex items-center gap-1 px-3 py-2 text-[10px] uppercase tracking-widest border border-[#EEDC82]/40 text-[#EEDC82] rounded hover:bg-[#EEDC82]/10">
            <Plus size={12} /> Produkt hinzufügen
          </button>
        </div>
      )}
      <ActionButtons tone="#EEDC82" onPreview={preview} onPrint={print} busy={busy} />
    </DialogShell>
  );
}

function StatusSignDialog({ onClose }) {
  const [kind, setKind] = useState("ausverkauft");
  const [headline, setHeadline] = useState("");
  const [subline, setSubline] = useState("");
  const [dateStr, setDateStr] = useState("");
  const [busy, setBusy] = useState(false);
  const [presets, setPresets] = useState([]);
  useEffect(() => { api.get("/admin/signage/presets").then((r) => setPresets(r.data.status_presets || [])).catch(() => {}); }, []);
  const build = () => ({ kind, custom_headline: headline, custom_subline: subline, date_str: dateStr });
  const preview = async () => { setBusy(true); try { await postPdfPreview("/admin/signage/status-sign", build()); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); } };
  const print = async () => { setBusy(true); try { await postPrint("/admin/signage/status-sign", build()); toast.success("Aushang an Drucker gesendet"); onClose(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Fehler"); } finally { setBusy(false); } };
  return (
    <DialogShell title="Status-Aushang" subtitle="A4 quer · Kassentür / Foyer / Saal" tone="#7A1921" icon={Ban} onClose={onClose} testid="ps-dialog-status">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
        {presets.map((p) => (
          <button key={p.kind} onClick={() => setKind(p.kind)}
            data-testid={`ps-status-${p.kind}`}
            className={`p-3 rounded border text-left transitions-safe ${kind === p.kind ? "border-[#D4AF37] bg-[#D4AF37]/[0.08]" : "border-[#2A2A2A] hover:border-[#575148]"}`}>
            <div className="text-2xl" style={{ color: p.color }}>{p.icon}</div>
            <div className="text-xs font-heading mt-1">{p.headline}</div>
          </button>
        ))}
      </div>
      <div className="space-y-3">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Überschrift (optional überschreiben)</label>
          <input value={headline} onChange={(e) => setHeadline(e.target.value)}
            placeholder={presets.find((p) => p.kind === kind)?.headline || ""}
            className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Unterzeile (optional)</label>
          <input value={subline} onChange={(e) => setSubline(e.target.value)}
            placeholder={presets.find((p) => p.kind === kind)?.subline || ""}
            className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Datum (optional, groß gedruckt)</label>
          <input value={dateStr} onChange={(e) => setDateStr(e.target.value)}
            placeholder="04.08.2026"
            className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2" />
        </div>
        <ActionButtons tone="#7A1921" onPreview={preview} onPrint={print} busy={busy} />
      </div>
    </DialogShell>
  );
}
